import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Truck } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export function AssignDialog({ open, onOpenChange, clients, availableItems, presetItemId }) {
  const qc = useQueryClient();
  const [inventoryId, setInventoryId] = useState(presetItemId || "");
  const [clientId, setClientId] = useState("");
  const [deliveryDate, setDeliveryDate] = useState(today());
  const [expectedReturnDate, setExpectedReturnDate] = useState("");
  const [notes, setNotes] = useState("");

  React.useEffect(() => {
    if (open) {
      setInventoryId(presetItemId || "");
      setClientId("");
      setDeliveryDate(today());
      setExpectedReturnDate("");
      setNotes("");
    }
  }, [open, presetItemId]);

  const mutation = useMutation({
    mutationFn: async () =>
      (await api.post("/rentals", {
        inventory_id: inventoryId,
        client_id: clientId,
        delivery_date: new Date(deliveryDate).toISOString(),
        expected_return_date: new Date(expectedReturnDate).toISOString(),
        notes,
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rentals"] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success("Delivery recorded");
      onOpenChange(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to assign"),
  });

  const submit = () => {
    if (!inventoryId || !clientId || !deliveryDate || !expectedReturnDate) {
      toast.error("Please fill all required fields");
      return;
    }
    mutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Truck className="h-5 w-5 text-[#0A2540]" /> New Delivery
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Inventory Item *</Label>
            <Select value={inventoryId} onValueChange={setInventoryId} disabled={!!presetItemId}>
              <SelectTrigger data-testid="assign-item-select"><SelectValue placeholder="Select available item" /></SelectTrigger>
              <SelectContent>
                {availableItems.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">No available items</div>}
                {availableItems.map((i) => (
                  <SelectItem key={i.id} value={i.id}>
                    {i.asset_tag} — {i.brand} {i.model_name} ({i.device_type})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Client *</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger data-testid="assign-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>
                {clients.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">No clients yet</div>}
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Delivery Date *</Label>
              <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} data-testid="assign-delivery-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Expected Return *</Label>
              <Input type="date" value={expectedReturnDate} min={deliveryDate} onChange={(e) => setExpectedReturnDate(e.target.value)} data-testid="assign-return-date" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional delivery notes" data-testid="assign-notes" />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline" data-testid="assign-cancel">Cancel</Button></DialogClose>
          <Button onClick={submit} disabled={mutation.isPending} className="bg-[#0A2540] hover:bg-[#1E3A5F]" data-testid="assign-submit">
            {mutation.isPending ? "Saving…" : "Record Delivery"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
